import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Menu, X, LayoutDashboard, LogOut, User, Shield, Search } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { ThemeToggle } from "@/components/ThemeToggle";
import { StoreBadges } from "@/components/StoreBadges";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const dashPath = user && user.role === "provider" ? "/provider" : user && user.role === "agent" ? "/agent" : "/dashboard";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    navigate(q ? `/browse?q=${encodeURIComponent(q)}` : "/browse");
    setOpen(false);
  };

  const isHome = location.pathname === "/";

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 ${isHome ? "bg-[#060810]/70" : "bg-[#060810]/90"} backdrop-blur-xl border-b border-[#1D2640]`}
    >
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 group shrink-0">
            <Logo size={36} tagline />
          </Link>

          <form
            onSubmit={submitSearch}
            data-testid="navbar-search-form"
            className="hidden md:flex items-center gap-2 flex-1 max-w-md mx-6 px-4 py-2 rounded-full bg-[#0B0E1B] border border-[#1D2640] focus-within:border-cyan-400/50 transition-colors"
          >
            <Search className="w-4 h-4 text-cyan-400 shrink-0" />
            <input
              data-testid="navbar-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tours, cars, islands…"
              className="bg-transparent text-white text-sm w-full outline-none placeholder:text-slate-500"
            />
          </form>

          <div className="hidden lg:flex items-center gap-6 shrink-0">
            <Link to="/browse" data-testid="navbar-browse-link" className="text-sm text-slate-300 hover:text-cyan-400 transition-colors">
              Browse
            </Link>
            <Link to="/cars" data-testid="navbar-cars-link" className="text-sm text-slate-300 hover:text-cyan-400 transition-colors">Cars</Link>
            <Link to="/browse?category=Tours" className="text-sm text-slate-300 hover:text-cyan-400 transition-colors">Tours</Link>
            <Link to="/blog" data-testid="navbar-blog-link" className="text-sm text-slate-300 hover:text-cyan-400 transition-colors">Journal</Link>
            <Link to="/community" data-testid="navbar-community-link" className="text-sm text-slate-300 hover:text-cyan-400 transition-colors">Community</Link>
            <Link to="/about" data-testid="navbar-about-link" className="text-sm text-slate-300 hover:text-cyan-400 transition-colors">About</Link>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <StoreBadges className="hidden xl:flex mr-1" />
            <ThemeToggle />
            {user ? (
              <>
                <NotificationBell />
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="navbar-admin-btn"
                    className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-full glass text-fuchsia-300 hover:text-white transition-colors">
                    <Shield className="w-4 h-4" /> Admin
                  </Link>
                )}
                {user.role === "traveler" && (
                  <Link to="/dashboard" data-testid="navbar-points"
                    className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-full glass text-cyan-300 hover:text-white transition-colors">
                    <span className="text-base leading-none">★</span>
                    <span className="font-heading font-bold">{(user.points ?? 0).toLocaleString()}</span>
                  </Link>
                )}
                <Link
                  to={dashPath}
                  data-testid="navbar-dashboard-btn"
                  className="flex items-center gap-2 text-sm px-4 py-2 rounded-full glass text-slate-200 hover:text-cyan-400 transition-colors"
                >
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Link>
                <Link
                  to="/profile"
                  data-testid="navbar-profile-btn"
                  className="flex items-center gap-2 text-sm px-4 py-2 rounded-full glass text-slate-200 hover:text-cyan-400 transition-colors"
                >
                  <User className="w-4 h-4" /> Profile
                </Link>
                <button
                  onClick={handleLogout}
                  data-testid="navbar-logout-btn"
                  className="flex items-center gap-2 text-sm px-4 py-2 rounded-full text-slate-400 hover:text-coral-400 hover:text-[#FF5E7E] transition-colors"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="navbar-login-btn" className="text-sm px-4 py-2 rounded-full text-slate-200 hover:text-cyan-400 transition-colors">
                  Log in
                </Link>
                <Link to="/register" data-testid="navbar-register-btn" className="text-sm px-5 py-2 rounded-full btn-neon font-semibold">
                  Get started
                </Link>
              </>
            )}
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle />
            <button onClick={() => setOpen(!open)} data-testid="navbar-mobile-toggle" className="text-white">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-[#1D2640] px-4 py-4 space-y-3">
          <form onSubmit={submitSearch} data-testid="navbar-mobile-search-form"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0B0E1B] border border-[#1D2640]">
            <Search className="w-4 h-4 text-cyan-400 shrink-0" />
            <input
              data-testid="navbar-mobile-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tours, cars, islands…"
              className="bg-transparent text-white text-sm w-full outline-none placeholder:text-slate-500"
            />
          </form>
          <Link to="/browse" onClick={() => setOpen(false)} className="block text-slate-200 py-1">Browse</Link>
          <Link to="/cars" onClick={() => setOpen(false)} className="block text-slate-200 py-1">Cars</Link>
          <Link to="/blog" onClick={() => setOpen(false)} className="block text-slate-200 py-1">Journal</Link>
          <Link to="/community" onClick={() => setOpen(false)} className="block text-slate-200 py-1">Community</Link>
          <Link to="/about" onClick={() => setOpen(false)} className="block text-slate-200 py-1">About</Link>
          <Link to="/faq" onClick={() => setOpen(false)} className="block text-slate-200 py-1">FAQ</Link>
          <div className="pt-2 border-t border-[#1D2640]">
            <p className="text-xs text-slate-500 mb-2">Get the Carivisor app</p>
            <StoreBadges />
          </div>
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 text-fuchsia-300 py-1">
                  <Shield className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link to={dashPath} onClick={() => setOpen(false)} className="flex items-center gap-2 text-slate-200 py-1">
                <User className="w-4 h-4" /> Dashboard
              </Link>
              <Link to="/profile" onClick={() => setOpen(false)} className="flex items-center gap-2 text-slate-200 py-1">
                <User className="w-4 h-4" /> Profile
              </Link>
              <button onClick={() => { handleLogout(); setOpen(false); }} className="flex items-center gap-2 text-[#FF5E7E] py-1">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          ) : (
            <div className="flex gap-3 pt-2">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center py-2 rounded-full glass text-slate-200">Log in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center py-2 rounded-full btn-neon font-semibold">Get started</Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
